import type { ApiSchemas } from '@rental/contracts/client'
import { LOCALES } from '@rental/domain'
import { e164 } from '../phone'
import type { StaffProblem } from '../problems'

/*
 * S8 (GDPR art. 16), "Modifica dati": what the correction forms decide by
 * themselves. A form starts from the record as it was read, with its
 * version. It sends only what the counter changed, so the audit row names
 * those fields and nothing else. A record changed meanwhile is read again
 * without losing what was typed. The rules checked here are the API's own
 * (CustomerCorrection, LicenceCorrection): the server checks them again.
 */

type Customer = ApiSchemas['CustomerDetail']['customer']
type Licence = ApiSchemas['CustomerDetail']['licences'][number]

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const COUNTRY = /^[A-Z]{2}$/
const PHONE = /^\+[1-9]\d{6,14}$/
const POSTAL_CODE = /^[A-Za-z0-9][A-Za-z0-9 -]{1,11}$/
/** A first look only: the API's own rule decides, and a refusal names the field. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The record a form started from, and the version it read. */
export interface Based<T> {
  version: number
  values: T
}

// ---------------------------------------------------------------------------
// The customer.

/** The customer's fields the form puts right, in the order it shows them (the API's). */
/** How a customer bills: as a person, or as a company. */
export const PARTY_TYPES = ['person', 'company'] as const

export const CUSTOMER_FIELDS = [
  'firstName',
  'lastName',
  'birthDate',
  'birthPlace',
  'nationality',
  'email',
  'phone',
  'addressLine',
  'postalCode',
  'city',
  'province',
  'country',
  'preferredLocale',
  'notes',
  // Fiscal identity carried over from the old site (CUS-1).
  'partyType',
  'vatNumber',
  'taxCode',
  'sdiCode',
  'pec',
] as const
export type CustomerField = (typeof CUSTOMER_FIELDS)[number]

/** How the customer is reached: gone for good once their contact is erased. */
export const CONTACT_FIELDS: readonly CustomerField[] = ['email', 'phone', 'notes', 'pec']

/** What the form holds: each field as typed, an empty one meaning none. */
export type CustomerDraft = Record<CustomerField, string>

export function customerDraftOf(customer: Customer): Based<CustomerDraft> {
  return {
    version: customer.version,
    values: {
      firstName: customer.firstName,
      lastName: customer.lastName,
      birthDate: customer.birthDate ?? '',
      birthPlace: customer.birthPlace ?? '',
      nationality: customer.nationality ?? '',
      email: customer.email ?? '',
      phone: customer.phone ?? '',
      addressLine: customer.addressLine ?? '',
      postalCode: customer.postalCode ?? '',
      city: customer.city ?? '',
      province: customer.province ?? '',
      country: customer.country ?? '',
      preferredLocale: customer.preferredLocale,
      notes: customer.notes ?? '',
      partyType: customer.partyType ?? '',
      vatNumber: customer.vatNumber ?? '',
      taxCode: customer.taxCode ?? '',
      sdiCode: customer.sdiCode ?? '',
      pec: customer.pec ?? '',
    },
  }
}

/** A field as the API keeps it: trimmed, a country in capitals, an address in small letters, a phone without spaces. */
function normalised(field: CustomerField, value: string): string {
  const trimmed = value.trim()
  switch (field) {
    case 'nationality':
    case 'country':
      return trimmed.toUpperCase()
    case 'email':
      return trimmed.toLowerCase()
    case 'phone':
      return e164(trimmed)
    default:
      return trimmed
  }
}

/** The fields the API would refuse, in the form's order. */
export function invalidCustomerFields(draft: CustomerDraft): CustomerField[] {
  const value = (field: CustomerField) => normalised(field, draft[field])
  const within = (field: CustomerField, min: number, max: number) =>
    value(field).length >= min && value(field).length <= max
  const optional = (field: CustomerField, valid: (text: string) => boolean) =>
    value(field) === '' || valid(value(field))
  const checks: Record<CustomerField, boolean> = {
    firstName: within('firstName', 1, 80),
    lastName: within('lastName', 1, 80),
    birthDate: optional('birthDate', (text) => ISO_DATE.test(text)),
    birthPlace: optional('birthPlace', (text) => text.length <= 100),
    nationality: optional('nationality', (text) => COUNTRY.test(text)),
    email: optional('email', (text) => text.length <= 254 && EMAIL.test(text)),
    phone: optional('phone', (text) => PHONE.test(text)),
    addressLine: optional('addressLine', (text) => text.length >= 3 && text.length <= 200),
    postalCode: optional('postalCode', (text) => POSTAL_CODE.test(text)),
    city: optional('city', (text) => text.length <= 100),
    province: optional('province', (text) => text.length <= 8),
    country: optional('country', (text) => COUNTRY.test(text)),
    preferredLocale: (LOCALES as readonly string[]).includes(value('preferredLocale')),
    notes: optional('notes', (text) => text.length <= 500),
    partyType: optional('partyType', (text) =>
      PARTY_TYPES.includes(text as (typeof PARTY_TYPES)[number]),
    ),
    vatNumber: optional('vatNumber', (text) => text.length <= 20),
    taxCode: optional('taxCode', (text) => text.length <= 32),
    sdiCode: optional('sdiCode', (text) => text.length <= 10),
    pec: optional('pec', (text) => text.length <= 254),
  }
  return CUSTOMER_FIELDS.filter((field) => !checks[field])
}

/**
 * The correction the form sends: the version read, and each field changed
 * since, emptied ones as null. Nothing else: a field the counter left alone
 * is not sent, so another person's change to it meanwhile is never undone.
 */
export function customerCorrection(
  base: Based<CustomerDraft>,
  draft: CustomerDraft,
): { body: ApiSchemas['CustomerCorrectionInput']; changed: CustomerField[] } {
  const body: Record<string, string | number | null> = {
    version: base.version,
  }
  const changed: CustomerField[] = []
  for (const field of CUSTOMER_FIELDS) {
    const now = normalised(field, draft[field])
    if (now === normalised(field, base.values[field])) continue
    changed.push(field)
    body[field] = now === '' ? null : now
  }
  return { body: body as ApiSchemas['CustomerCorrectionInput'], changed }
}

// ---------------------------------------------------------------------------
// A licence.

export const LICENCE_FIELDS = [
  'number',
  'issuingCountry',
  'expiresOn',
  'categories',
  'hasIdp',
] as const
export type LicenceField = (typeof LICENCE_FIELDS)[number]

/** A category as typed: its code and the day it was first obtained (field 10), if known. */
export interface CategoryDraft {
  code: string
  firstIssuedOn: string
}

export interface LicenceDraft {
  number: string
  issuingCountry: string
  expiresOn: string
  categories: CategoryDraft[]
  hasIdp: boolean
}

export function licenceDraftOf(licence: Licence): Based<LicenceDraft> {
  return {
    version: licence.version,
    values: {
      number: licence.number,
      issuingCountry: licence.issuingCountry,
      expiresOn: licence.expiresOn ?? '',
      categories: licence.categories.map((held) => ({
        code: held.code,
        firstIssuedOn: held.firstIssuedOn ?? '',
      })),
      hasIdp: licence.hasIdp,
    },
  }
}

/** The categories as the API keeps them: a code in capitals, an empty row left out. */
function heldCategories(rows: readonly CategoryDraft[]): CategoryDraft[] {
  return rows
    .map((row) => ({
      code: row.code.trim().toUpperCase(),
      firstIssuedOn: row.firstIssuedOn,
    }))
    .filter((row) => row.code !== '')
}

/** The fields the API would refuse, in the form's order. */
export function invalidLicenceFields(draft: LicenceDraft): LicenceField[] {
  const number = draft.number.trim()
  const held = heldCategories(draft.categories)
  const checks: Record<LicenceField, boolean> = {
    number: number.length >= 3 && number.length <= 40,
    issuingCountry: COUNTRY.test(draft.issuingCountry.trim().toUpperCase()),
    expiresOn: draft.expiresOn === '' || ISO_DATE.test(draft.expiresOn),
    categories:
      held.length >= 1 &&
      held.length <= 20 &&
      held.every(
        (row) =>
          row.code.length <= 10 && (row.firstIssuedOn === '' || ISO_DATE.test(row.firstIssuedOn)),
      ),
    hasIdp: true,
  }
  return LICENCE_FIELDS.filter((field) => !checks[field])
}

/** The licence correction: the version read and each field changed since (categories whole). */
export function licenceCorrection(
  base: Based<LicenceDraft>,
  draft: LicenceDraft,
): { body: ApiSchemas['LicenceCorrectionInput']; changed: LicenceField[] } {
  const body: ApiSchemas['LicenceCorrectionInput'] = { version: base.version }
  const changed: LicenceField[] = []
  const number = draft.number.trim()
  if (number !== base.values.number.trim()) {
    changed.push('number')
    body.number = number
  }
  const country = draft.issuingCountry.trim().toUpperCase()
  if (country !== base.values.issuingCountry.trim().toUpperCase()) {
    changed.push('issuingCountry')
    body.issuingCountry = country
  }
  if (draft.expiresOn !== base.values.expiresOn) {
    changed.push('expiresOn')
    body.expiresOn = draft.expiresOn === '' ? null : draft.expiresOn
  }
  const held = heldCategories(draft.categories)
  if (JSON.stringify(held) !== JSON.stringify(heldCategories(base.values.categories))) {
    changed.push('categories')
    body.categories = held.map((row) =>
      row.firstIssuedOn === ''
        ? { code: row.code }
        : { code: row.code, firstIssuedOn: row.firstIssuedOn },
    )
  }
  if (draft.hasIdp !== base.values.hasIdp) {
    changed.push('hasIdp')
    body.hasIdp = draft.hasIdp
  }
  return { body, changed }
}

// ---------------------------------------------------------------------------
// Both.

/**
 * The form after its record was read again (the version it held was not the
 * record's any more): each field the counter changed keeps what they typed,
 * every other takes the record's value now, and the version is the new one,
 * so the next save is checked against what they now see. `reset` fields take
 * the record's value whatever was typed: a contact erased meanwhile cannot be
 * written back. `clashed`: the fields changed on both sides, which the
 * counter checks before saving again.
 */
export function rebase<T extends object>(
  base: Based<T>,
  draft: T,
  fresh: Based<T>,
  reset: readonly (keyof T)[] = [],
): { base: Based<T>; draft: T; kept: (keyof T)[]; clashed: (keyof T)[] } {
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
  const next = { ...fresh.values }
  const kept: (keyof T)[] = []
  const clashed: (keyof T)[] = []
  for (const key of Object.keys(fresh.values) as (keyof T)[]) {
    if (reset.includes(key) || same(draft[key], base.values[key])) continue
    next[key] = draft[key]
    kept.push(key)
    if (!same(fresh.values[key], base.values[key])) clashed.push(key)
  }
  return { base: fresh, draft: next, kept, clashed }
}

/**
 * The fields a refusal of the request's shape names (400 validation_failed),
 * in the form's order: the API's rule is the last word, and the form marks
 * each field it named.
 */
export function refusedFields<F extends string>(
  problem: StaffProblem | null,
  fields: readonly F[],
): F[] {
  if (problem?.kind !== 'api' || problem.code !== 'validation_failed') return []
  const issues = problem.details.issues
  if (!Array.isArray(issues)) return []
  const named = new Set<string>()
  for (const issue of issues) {
    const path = (issue as { path?: unknown } | null)?.path
    if (typeof path === 'string') named.add(path.split('/')[1] ?? '')
  }
  return fields.filter((field) => named.has(field))
}
